import { CaretUpDownIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { CircleCheckIcon, CircleXIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useEventParticipants,
  useEventParticipantTypes,
} from '@/modules/events/api';
import { ParticipantUsageLeftInput } from '@/modules/events/components/participant-usage-left-input';
import { EventParticipant } from '@/modules/eventsV2/types';
import { EventFiltersType } from '@/modules/eventsV2/hooks';

import { ParticipantNotifications } from '../components/participant-notifications';
import { ParticipantTableActions } from '../components/participant-table-actions';
import { ParticipantTypeSelect } from '../components/participant-type-select';

export const useParticipantsTab = (filters: EventFiltersType) => {
  const params = useParams<{ id: string }>();

  const [isUploading, setIsUploading] = useState(false);

  const { getValues } = useFormContext();
  const eventId = params.id;
  const eventName = getValues('name');

  const queryClient = useQueryClient();

  const {
    isFetching,
    participants,
    uploadEventParticipants,
    isGeneratingReport,
    getGenerateReportUrl,
  } = useEventParticipants(eventId, eventName, filters);
  const { participantTypes, isFetching: isFetchingParticipantTypes } =
    useEventParticipantTypes(eventId, true);

  const uploadParticipant = async (file: File) => {
    setIsUploading(true);
    try {
      await uploadEventParticipants(file);
      await queryClient.invalidateQueries({ queryKey: ['eventParticipants'] });
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const pickFileAndUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.length) return;

    const file = event.target.files[0];

    await uploadParticipant(file);
  };

  const participantTypesOptions = useMemo(() => {
    return participantTypes.map((type) => ({
      id: type.id,
      value: type.value,
    }));
  }, [participantTypes]);

  const formattedParticipants = useMemo(
    () =>
      participants.map((participant) => {
        return {
          ...participant,
          fullName: `${participant.firstName} ${participant.lastName}`,
        };
      }),
    [participants]
  );

  const columns: ColumnDef<EventParticipant>[] = [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          className="w-5 h-5"
          onCheckedChange={table.toggleAllRowsSelected}
          checked={table.getIsAllRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <div className="px-1">
          <Checkbox
            className="w-5 h-5"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={row.toggleSelected}
          />
        </div>
      ),
    },
    {
      size: 130,
      accessorKey: 'firstName',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            First name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.firstName}</span>
      ),
    },
    {
      size: 130,
      accessorKey: 'lastName',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Last name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.lastName}</span>
      ),
    },
    {
      size: 130,
      accessorKey: 'externalId',
      header: 'Has external ID',
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {row.original.externalId ? (
            <CircleCheckIcon color={colors.green[400]} size={28} />
          ) : (
            <CircleXIcon color="hsl(var(--destructive))" size={28} />
          )}
        </span>
      ),
    },
    {
      size: 240,
      accessorKey: 'email',
      header: 'Email Address',
    },
    {
      size: 100,
      accessorKey: 'usagesLeft',
      header: 'Attempts left',
      cell: ({ row }) => (
        <ParticipantUsageLeftInput
          eventId={eventId}
          participantId={row.original.id}
          defaultValue={row.original.usagesLeft.toString()}
        />
      ),
    },
    {
      size: 200,
      accessorKey: 'company',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Company
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.company}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'jobTitle',
      header: 'Job Title',
    },
    {
      accessorKey: 'status',
      header: 'Status',
    },
    {
      size: 200,
      accessorKey: 'phoneNumber',
      header: 'Phone Number',
    },
    {
      size: 140,
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <ParticipantTypeSelect
          eventId={eventId}
          participantId={row.original.id}
          defaultValue={row.original.type}
          participantTypes={participantTypes}
        />
      ),
    },
    {
      size: 200,
      accessorKey: 'comment',
      header: 'Other',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="line-clamp-2">{row.original.comment}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{row.original.comment}</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      size: 260,
      accessorKey: 'notificationNumbers',
      header: 'Alert Recipients',
      cell: ({ row }) => (
        <ParticipantNotifications
          notificationNumbers={row.original.notificationNumbers}
        />
      ),
    },
    {
      size: 40,
      id: 'actions',
      header: 'Actions',
      enableHiding: false,
      cell: ({ row }) => (
        <ParticipantTableActions
          eventId={eventId}
          participantId={row.original.id}
          externalId={row.original.externalId}
        />
      ),
    },
  ];

  return {
    eventId,
    columns,
    participants,
    participantTypesOptions,
    participantTypes,
    formattedParticipants,
    isLoading: isFetching || isUploading || isFetchingParticipantTypes,
    pickFileAndUpload,
    isGeneratingReport,
    getGenerateReportUrl,
  };
};
